import { useState } from 'react'
import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography
} from '@mui/material'
import { styled } from '@mui/material/styles'
import ButtonBase from 'design-system/button'
import { useLogo } from 'hooks/useLogo'
import { XRAY_MANUAL } from 'navigation/CONSTANTS'
import analytics from 'services/analytics'
import { acceptXrayManual } from 'services/user'
import Storage from 'storage/auth'
import { IXrayManualConfirmation } from 'types'
import { XRAY_MANUAL_CONFIRMATIONS } from './constants'
import { useFullHeightIframe } from './useFullHeightIframe'

const Section = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(2),
  maxWidth: '760px',
  padding: theme.spacing(4, 5),
  margin: '0 auto',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3, 2)
  }
}))

const ManualFrame = styled('iframe')(({ theme }) => ({
  display: 'block',
  width: '100%',
  height: '100vh',
  border: 'none',
  borderTop: `1px solid ${theme.palette.divider}`,
  borderBottom: `1px solid ${theme.palette.divider}`,
  background: theme.palette.background.default
}))

const ERROR_MESSAGE = 'Não foi possível registrar o aceite. Tente novamente.'

interface AcceptXrayManualProps {
  onAccepted: () => void
}

/**
 * Shown in place of the x-ray module while the professional has not accepted
 * the x-ray manual yet. The manual is shown full width and fully expanded; the
 * confirmation below it unlocks only after the page is scrolled to the manual's
 * end, and there is no way to refuse or skip.
 */
const AcceptXrayManual = ({ onAccepted }: AcceptXrayManualProps) => {
  const logo = useLogo()
  const { iframeRef, height, reachedEnd } = useFullHeightIframe()
  const [checked, setChecked] = useState<IXrayManualConfirmation[]>([])
  const [status, setStatus] = useState<'idle' | 'submitting' | 'error'>(
    'idle'
  )

  const allChecked = XRAY_MANUAL_CONFIRMATIONS.every(({ id }) =>
    checked.includes(id)
  )

  const toggle = (id: IXrayManualConfirmation, value: boolean) => {
    setChecked(current =>
      value ? [...current, id] : current.filter(item => item !== id)
    )
  }

  const handleAccept = async () => {
    setStatus('submitting')
    try {
      const response = await acceptXrayManual(
        XRAY_MANUAL_CONFIRMATIONS.map(({ id }) => id)
      )
      if (!response.success) {
        setStatus('error')
        return
      }
      const user = Storage.user.get()
      if (user) {
        Storage.user.set({ ...user, xray_manual_accepted: true })
      }
      analytics.track('xray_manual_accepted', {})
      onAccepted()
    } catch {
      setStatus('error')
    }
  }

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Section>
        <img src={logo} alt='sentinela' style={{ width: '240px' }} />
        <Typography variant='h6' component='h1' textAlign='center'>
          Manual de uso do raio-x
        </Typography>
        <Typography
          variant='subtitle1'
          color='textPrimary'
          fontWeight='bold'
          textAlign='center'
        >
          Para continuar,{' '}
          <Box component='strong' sx={{ color: 'primary.main' }}>
            leia o manual até o final
          </Box>{' '}
          e confirme o item abaixo.
        </Typography>
      </Section>

      <ManualFrame
        ref={iframeRef}
        title='Manual de raio-x'
        src={XRAY_MANUAL}
        style={height === undefined ? undefined : { height }}
      />

      <Section>
        {!reachedEnd && (
          <Alert severity='info' sx={{ width: '100%' }}>
            Role o manual até o final para habilitar a confirmação.
          </Alert>
        )}

        <FormGroup sx={{ alignSelf: 'flex-start', gap: 1 }}>
          {XRAY_MANUAL_CONFIRMATIONS.map(({ id, label }) => (
            <FormControlLabel
              key={id}
              disabled={!reachedEnd}
              control={
                <Checkbox
                  checked={checked.includes(id)}
                  onChange={(_, value) => toggle(id, value)}
                />
              }
              label={label}
            />
          ))}
        </FormGroup>

        {status === 'error' && (
          <Alert severity='error' sx={{ width: '100%' }}>
            {ERROR_MESSAGE}
          </Alert>
        )}

        <ButtonBase
          onClick={handleAccept}
          disabled={!allChecked || status === 'submitting'}
          sx={{ width: '100%' }}
        >
          Aceitar e continuar
        </ButtonBase>
      </Section>
    </Box>
  )
}

export default AcceptXrayManual
